import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

/* ─── Palette aligned with Desktop.jsx card redesign ─── */
const P = {
  red: "#DC2626",
  redBg: "#FEF2F2",
  redBorder: "#FECACA",
  text: "#111",
  subtle: "#9CA3AF",
  subtleMid: "#6B7280",
  border: "#F0F0F0",
  borderMid: "#E5E7EB",
  surface: "#fff",
  surfaceAlt: "#FAFAFA",
  chip: "#F7F7F8",
  chipBorder: "#EFEFEF",
};

/* ─── Utility ─── */
const normalize = (v = "") => v.toLowerCase().trim();
const matchesPoint = (point, query) => {
  if (!query) return true;
  const q = normalize(query);
  return [point?.point, point?.time, point?.description, point?.city, point?.label, point?.name]
    .filter(Boolean)
    .some((v) => normalize(String(v)).includes(q));
};

/* ─── Search icon ─── */
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
      fill={P.subtle}
      fillRule="evenodd"
      clipRule="evenodd"
    />
  </svg>
);

/* ─── Check icon ─── */
const CheckIcon = () => (
  <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
    <path d="M2 5l2.5 2.5L8 3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ─── Single point row ─── */
const PointRow = ({ point, selected, onSelect, mode, isLast, forceBottomBorder }) => (
  <button
    type="button"
    onClick={() => onSelect(point)}
    style={{
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "11px 14px",
      borderRadius: 0,
      border: "none",
      borderBottom: !isLast || forceBottomBorder ? `1px solid ${P.border}` : "none",
      background: selected ? P.redBg : P.surface,
      cursor: "pointer",
      textAlign: "left",
      transition: "background 0.12s",
      boxSizing: "border-box",
    }}
  >
    {/* Radio — LEFT */}
    <span
      style={{
        width: 17,
        height: 17,
        borderRadius: "50%",
        border: `2px solid ${selected ? P.red : P.borderMid}`,
        background: selected ? P.red : P.surface,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "border-color 0.12s, background 0.12s",
      }}
    >
      {selected && <CheckIcon />}
    </span>

    {/* Name + description — CENTER */}
    <div style={{ flex: 1, minWidth: 0 }}>
      <p
        style={{
          fontSize: 13,
          fontWeight: selected ? 700 : 600,
          color: selected ? P.red : P.text,
          margin: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          letterSpacing: "-0.01em",
          lineHeight: 1.35,
        }}
      >
        {point.point}
      </p>
      {(point.description || point.city) && (
        <p
          style={{
            fontSize: 10,
            color: P.subtle,
            margin: "2px 0 0",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: 1.3,
          }}
        >
          {point.description || point.city}
        </p>
      )}
    </div>

    {/* Time — RIGHT */}
    {point.time && (
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: selected ? P.red : P.subtleMid,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.01em",
          flexShrink: 0,
        }}
      >
        {point.time}
      </span>
    )}

    <input type="radio" name={`point-${mode}`} checked={!!selected} onChange={() => {}} style={{ display: "none" }} />
  </button>
);

/* ─── Main ─── */
const PointSelection = ({
  boardingPoints,
  droppingPoints,
  selectedBoardingPoint,
  setSelectedBoardingPoint,
  selectedDroppingPoint,
  setSelectedDroppingPoint,
}) => {
  const [activeTab, setActiveTab] = useState("boarding");
  const [boardingSearch, setBoardingSearch] = useState("");
  const [droppingSearch, setDroppingSearch] = useState("");

  const boardingDone = !!selectedBoardingPoint;
  const droppingDone = !!selectedDroppingPoint;

  const filteredBoarding = useMemo(
    () => (Array.isArray(boardingPoints) ? boardingPoints.filter((p) => matchesPoint(p, boardingSearch)) : []),
    [boardingPoints, boardingSearch]
  );

  const filteredDropping = useMemo(
    () => (Array.isArray(droppingPoints) ? droppingPoints.filter((p) => matchesPoint(p, droppingSearch)) : []),
    [droppingPoints, droppingSearch]
  );

  const isBoardingSelected = (p) =>
    selectedBoardingPoint &&
    selectedBoardingPoint.point === p.point &&
    (selectedBoardingPoint.time ? selectedBoardingPoint.time === p.time : true);

  const isDroppingSelected = (p) =>
    selectedDroppingPoint &&
    selectedDroppingPoint.point === p.point &&
    (selectedDroppingPoint.time ? selectedDroppingPoint.time === p.time : true);

  const handleBoardingSelect = (point) => {
    setSelectedBoardingPoint(point);
    setBoardingSearch("");
    // Auto-switch to dropping tab after boarding picked
    if (!droppingDone) setActiveTab("dropping");
  };

  const handleDroppingSelect = (point) => {
    setSelectedDroppingPoint(point);
    setDroppingSearch("");
  };

  const activeList = activeTab === "boarding" ? filteredBoarding : filteredDropping;
  const hasSinglePoint = activeList.length === 1;
  const hasNoPoints = activeList.length === 0;

  return (
    <div style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
      {/* ── Structured inner selector panel ── */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          background: P.surface,
          border: `1px solid ${P.borderMid}`,
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
        }}
      >
        {/* ── Tab bar — Abhibus style ── */}
        <div
          style={{
            display: "flex",
            borderBottom: `1px solid ${P.border}`,
            background: P.surface,
            flexShrink: 0,
          }}
        >
          {[
            { key: "boarding", label: "Boarding Points", done: boardingDone, selected: selectedBoardingPoint },
            { key: "dropping", label: "Dropping Points", done: droppingDone, selected: selectedDroppingPoint },
          ].map(({ key, label, done, selected }, index, arr) => {
            const active = activeTab === key;
            const isLast = index === arr.length - 1;

            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  padding: "10px 14px 9px",
                  background: active ? "#FFF9F9" : "none",
                  border: "none",
                  borderBottom: `2.5px solid ${active ? P.red : "transparent"}`,
                  borderRight: !isLast ? `1px solid ${P.border}` : "none",
                  cursor: "pointer",
                  transition: "border-color 0.15s, background 0.15s",
                  marginBottom: -1,
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: done ? "#16A34A" : active ? P.red : P.borderMid,
                      display: "inline-block",
                      flexShrink: 0,
                      transition: "background 0.15s",
                    }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: active ? P.text : P.subtle,
                      letterSpacing: "-0.01em",
                      transition: "color 0.15s",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </span>
                </div>

                {done && selected && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#059669",
                      paddingLeft: 14,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                    }}
                  >
                    {selected.point}
                    {selected.time && (
                      <span style={{ color: "#6B7280", fontWeight: 500 }}> · {selected.time}</span>
                    )}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Search section ── */}
        <div
          style={{
            padding: "10px 12px",
            borderBottom: `1px solid ${P.border}`,
            background: P.surfaceAlt,
            flexShrink: 0,
          }}
        >
          <div style={{ position: "relative" }}>
            <div
              style={{
                position: "absolute",
                left: 11,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              <SearchIcon />
            </div>
            <input
              key={activeTab}
              type="text"
              value={activeTab === "boarding" ? boardingSearch : droppingSearch}
              placeholder={activeTab === "boarding" ? "Search Boarding Point" : "Search Dropping Point"}
              onChange={(e) =>
                activeTab === "boarding"
                  ? setBoardingSearch(e.target.value)
                  : setDroppingSearch(e.target.value)
              }
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "9px 12px 9px 32px",
                borderRadius: 9,
                border: `1px solid ${P.border}`,
                background: P.surface,
                fontSize: 12,
                fontWeight: 500,
                color: P.text,
                outline: "none",
                transition: "border-color 0.15s",
              }}
              onFocus={(e) => (e.target.style.borderColor = P.redBorder)}
              onBlur={(e) => (e.target.style.borderColor = P.border)}
            />
          </div>
        </div>

        {/* ── List section wrapper ── */}
        <div
          style={{
            flex: hasSinglePoint ? "0 0 auto" : 1,
            minHeight: 0,
            padding: 0,
            background: P.surface,
          }}
        >
          <div
            style={{
              height: hasSinglePoint ? "auto" : "100%",
              minHeight: hasNoPoints ? 210 : hasSinglePoint ? "auto" : 210,
              maxHeight: hasSinglePoint ? "none" : 320,
              overflowY: hasSinglePoint ? "visible" : "auto",
              background: P.surface,
            }}
          >
            {activeTab === "boarding" ? (
              filteredBoarding.length === 0 ? (
                <div style={{ padding: "28px 16px", textAlign: "center" }}>
                  <p style={{ fontSize: 12, color: P.subtle, margin: 0 }}>
                    {boardingSearch ? "No matching boarding points." : "No boarding points available."}
                  </p>
                </div>
              ) : (
                filteredBoarding.map((point, index) => (
                  <PointRow
                    key={point._id || `${point.point}-${point.time}-${index}`}
                    point={point}
                    selected={isBoardingSelected(point)}
                    onSelect={handleBoardingSelect}
                    mode="boarding"
                    isLast={index === filteredBoarding.length - 1}
                    forceBottomBorder={filteredBoarding.length === 1}
                  />
                ))
              )
            ) : filteredDropping.length === 0 ? (
              <div style={{ padding: "28px 16px", textAlign: "center" }}>
                <p style={{ fontSize: 12, color: P.subtle, margin: 0 }}>
                  {droppingSearch ? "No matching dropping points." : "No dropping points available."}
                </p>
              </div>
            ) : (
              filteredDropping.map((point, index) => (
                <PointRow
                  key={point._id || `${point.point}-${point.time}-${index}`}
                  point={point}
                  selected={isDroppingSelected(point)}
                  onSelect={handleDroppingSelect}
                  mode="dropping"
                  isLast={index === filteredDropping.length - 1}
                  forceBottomBorder={filteredDropping.length === 1}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── PropTypes ─── */
PointSelection.propTypes = {
  boardingPoints: PropTypes.array,
  droppingPoints: PropTypes.array,
  selectedBoardingPoint: PropTypes.object,
  setSelectedBoardingPoint: PropTypes.func.isRequired,
  selectedDroppingPoint: PropTypes.object,
  setSelectedDroppingPoint: PropTypes.func.isRequired,
};

PointRow.propTypes = {
  point: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  isLast: PropTypes.bool,
  forceBottomBorder: PropTypes.bool,
};

export default PointSelection;
